import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ApiResponse } from '../api/api-response';
import { AplicacaoConferenciaResultado, AtendimentoConferencia, CupomPessoa, CupomSugestao, EventoCupom, FiltrosCupom, RegraStatus, ResultadoCupom, UsosPeriodo } from './conferencia.model';

@Injectable({ providedIn: 'root' })
export class ConferenciaService {
  private readonly http = inject(HttpClient);
  private readonly url = `${environment.apiUrl}/Conferencia`;

  buscar(busca: string): Observable<ApiResponse<CupomPessoa[]>> {
    return this.http.get<ApiResponse<CupomPessoa[]>>(`${this.url}/buscar/${encodeURIComponent(busca)}`);
  }

  buscarCupons(q: string, take = 10, somenteManual = false): Observable<ApiResponse<CupomSugestao[]>> {
    let params = new HttpParams().set('q', q).set('take', String(take)).set('somenteManual', String(somenteManual));
    return this.http.get<ApiResponse<CupomSugestao[]>>(`${this.url}/cupons`, { params });
  }

  emitirManual(busca: string, nrSeqCupom: number): Observable<ApiResponse<boolean>> {
    return this.http.post<ApiResponse<boolean>>(
      `${this.url}/${encodeURIComponent(busca)}/emitir-manual`, { nrSeqCupom });
  }

  reativar(nrSeqCupomPaciente: number): Observable<ApiResponse<boolean>> {
    return this.http.put<ApiResponse<boolean>>(
      `${this.url}/cupom-paciente/${nrSeqCupomPaciente}/reativar`, {});
  }

  historico(nrSeqCupomPaciente: number): Observable<ApiResponse<EventoCupom[]>> {
    return this.http.get<ApiResponse<EventoCupom[]>>(`${this.url}/cupom-paciente/${nrSeqCupomPaciente}/historico`);
  }

  /** Tira da conta provisoria o desconto do cupom e o reativa (o back acha a conta). */
  removerDaConta(nrSeqCupomPaciente: number): Observable<ApiResponse<{ cdResultado: string; dsResultado: string }>> {
    return this.http.put<ApiResponse<{ cdResultado: string; dsResultado: string }>>(
      `${this.url}/cupom-paciente/${nrSeqCupomPaciente}/remover-da-conta`, {});
  }

  listarPorCupom(nrSeqCupom: number, filtros: FiltrosCupom): Observable<ApiResponse<ResultadoCupom>> {
    let params = new HttpParams();
    if (filtros.status && filtros.status !== 'Todos') params = params.set('status', filtros.status);
    if (filtros.periodoDias != null) params = params.set('periodoDias', String(filtros.periodoDias));
    if (filtros.dataIni) params = params.set('dataIni', filtros.dataIni);
    if (filtros.dataFim) params = params.set('dataFim', filtros.dataFim);
    if (filtros.cdEstabelecimento != null) params = params.set('cdEstabelecimento', String(filtros.cdEstabelecimento));
    params = params.set('skip', String(filtros.skip ?? 0));
    params = params.set('take', String(filtros.take ?? 50));
    return this.http.get<ApiResponse<ResultadoCupom>>(`${this.url}/cupom/${nrSeqCupom}`, { params });
  }

  exportarCupom(nrSeqCupom: number, filtros: Omit<FiltrosCupom, 'skip' | 'take'>): Observable<Blob> {
    let params = new HttpParams();
    if (filtros.status && filtros.status !== 'Todos') params = params.set('status', filtros.status);
    if (filtros.periodoDias != null) params = params.set('periodoDias', String(filtros.periodoDias));
    return this.http.get(`${this.url}/cupom/${nrSeqCupom}/export`, { params, responseType: 'blob' });
  }

  buscarPorAtendimento(nrAtendimento: number): Observable<ApiResponse<AtendimentoConferencia>> {
    return this.http.get<ApiResponse<AtendimentoConferencia>>(`${this.url}/atendimento/${nrAtendimento}`);
  }

  aplicarNoAtendimento(nrAtendimento: number, nrSeqCupomPaciente: number): Observable<ApiResponse<AplicacaoConferenciaResultado>> {
    return this.http.post<ApiResponse<AplicacaoConferenciaResultado>>(`${this.url}/atendimento/${nrAtendimento}/aplicar`, { nrSeqCupomPaciente });
  }

  regrasCupom(nrSeqCupom: number): Observable<ApiResponse<RegraStatus[]>> {
    return this.http.get<ApiResponse<RegraStatus[]>>(`${this.url}/cupom/${nrSeqCupom}/regras`);
  }

  usosPorDia(nrSeqCupom: number, dias: number, dataIni?: string, dataFim?: string, agruparPor?: 'dia' | 'semana' | 'mes'): Observable<ApiResponse<UsosPeriodo>> {
    let params = new HttpParams().set('dias', String(dias));
    if (dataIni) params = params.set('dataIni', dataIni);
    if (dataFim) params = params.set('dataFim', dataFim);
    if (agruparPor) params = params.set('agruparPor', agruparPor);
    return this.http.get<ApiResponse<UsosPeriodo>>(`${this.url}/cupom/${nrSeqCupom}/usos-por-dia`, { params });
  }
}
